import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Clock,
  Loader2,
  Plus,
  Trash2,
  Workflow,
  XCircle,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { CreateSbaCalendarDialog } from '@/components/CltCalendars/CreateSbaCalendarDialog'
import {
  useSbaCalendars,
  useDeleteSbaCalendar,
  useSetSbaCalendarsTemplateBulk,
} from '@/hooks/useSbaCalendars'
import { useTemplateSummaries } from '@/hooks/useCltTemplates'
import { cn } from '@/utils/cn'
import type { SbaCalendarRead } from '@/api/clt.types'

const STATUS_VARIANT: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  ready: 'default',
  computing: 'secondary',
  pending: 'outline',
  error: 'destructive',
  failed: 'destructive',
}

const STATUS_ICON: Record<string, LucideIcon> = {
  ready: CheckCircle2,
  computing: Loader2,
  pending: Clock,
  error: XCircle,
  failed: XCircle,
}

const formatStatus = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

type CalendarType = 'sba' | 'seasonal'

/**
 * Instantiated calendars list. Clicking a card opens the detail view.
 * Supply Chain Event Calendars support Calendar Logic Templates; Seasonal Calendars use fixed dates.
 */
export default function AdminCltCalendars() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const calendarType = (searchParams.get('type') ?? 'sba') as CalendarType
  const isSba = calendarType === 'sba'

  const {
    data: calendars = [],
    isLoading,
    isError,
  } = useSbaCalendars({ is_sba: isSba })
  const { data: summaries = [] } = useTemplateSummaries()
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<SbaCalendarRead | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkTemplateId, setBulkTemplateId] = useState<string>('')
  const deleteMutation = useDeleteSbaCalendar()
  const bulkMutation = useSetSbaCalendarsTemplateBulk()

  const templateName = (id: string | null | undefined) =>
    id ? (summaries.find(t => t.id === id)?.name ?? 'Unknown template') : null

  const defaultTemplateName =
    summaries.find(t => t.isDefault)?.name ?? 'No default'

  const toggleSelect = (id: string) =>
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const handleApply = async () => {
    if (!bulkTemplateId || selectedIds.size === 0) return
    try {
      await bulkMutation.mutateAsync({
        calendarIds: Array.from(selectedIds),
        templateId: bulkTemplateId,
      })
      setSelectedIds(new Set())
      setBulkTemplateId('')
    } catch {
      // total failure — keep selection so user can retry
    }
  }

  const handleTypeChange = (value: string) => {
    setSearchParams({ type: value })
    setSelectedIds(new Set())
    setBulkTemplateId('')
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Calendars</h1>
          <p className="mt-1 text-base text-muted-foreground">
            {isSba
              ? 'For each event, choose which Calendar Logic Template applies, and the dates instantiate from it.'
              : 'Seasonal Calendars define fixed dates for each milestone, reading from GBQ.'}
          </p>
        </div>
        {import.meta.env.VITE_ENABLE_CLT_CALENDAR_CREATE === 'true' && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Calendar
          </Button>
        )}
      </div>

      <Tabs value={calendarType} onValueChange={handleTypeChange}>
        <TabsList>
          <TabsTrigger value="sba">Supply Chain Event Calendars</TabsTrigger>
          <TabsTrigger value="seasonal">Seasonal Calendars</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          Loading calendars…
        </p>
      ) : isError ? (
        <p className="py-12 text-center text-sm text-destructive">
          Failed to load calendars.
        </p>
      ) : calendars.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          No calendars yet.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {calendars.map(cal => {
            const applied =
              templateName(cal.default_logic_template_id) ??
              `Default · ${defaultTemplateName}`
            return (
              <Card
                key={cal.id}
                className={cn(
                  'cursor-pointer transition-colors hover:border-primary/40',
                  isSba &&
                    selectedIds.has(cal.id) &&
                    'border-primary ring-1 ring-primary'
                )}
                onClick={() => navigate(`/admin/clt-calendars/${cal.id}`)}
              >
                <CardHeader>
                  <div className="flex items-start gap-3">
                    {isSba && (
                      <div
                        className="mt-0.5 shrink-0"
                        onClick={e => {
                          e.stopPropagation()
                          toggleSelect(cal.id)
                        }}
                      >
                        <Checkbox
                          checked={selectedIds.has(cal.id)}
                          onCheckedChange={() => toggleSelect(cal.id)}
                          aria-label={`Select ${cal.name}`}
                        />
                      </div>
                    )}
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                      <CalendarDays className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base">{cal.name}</CardTitle>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <CardDescription>
                          {cal.year != null ? `Year ${cal.year}` : '—'}
                        </CardDescription>
                        {cal.status &&
                          (() => {
                            const Icon = STATUS_ICON[cal.status] ?? Clock
                            return (
                              <Badge
                                variant={
                                  STATUS_VARIANT[cal.status] ?? 'secondary'
                                }
                                className="gap-1 text-[11px]"
                              >
                                <Icon
                                  className={cn(
                                    'h-3 w-3',
                                    cal.status === 'computing' && 'animate-spin'
                                  )}
                                />
                                {formatStatus(cal.status)}
                              </Badge>
                            )
                          })()}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 cursor-pointer text-muted-foreground hover:text-destructive"
                      onClick={e => {
                        e.stopPropagation()
                        setDeleteTarget(cal)
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                {isSba && (
                  <CardContent>
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <span className="flex min-w-0 items-center gap-1.5 text-muted-foreground">
                        <Workflow className="h-4 w-4 shrink-0" />
                        <span className="truncate">{applied}</span>
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </div>
                  </CardContent>
                )}
                {!isSba && (
                  <CardContent>
                    <div className="flex items-center justify-end gap-2 text-sm">
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </div>
                  </CardContent>
                )}
              </Card>
            )
          })}
        </div>
      )}

      <CreateSbaCalendarDialog open={createOpen} onOpenChange={setCreateOpen} />

      {isSba && selectedIds.size > 0 && (
        <div className="sticky bottom-0 -mx-6 -mb-6 border-t bg-card px-6 py-3 shadow-[0_-1px_4px_rgba(0,0,0,0.08)]">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="secondary" className="shrink-0">
              {selectedIds.size} calendar{selectedIds.size > 1 ? 's' : ''}{' '}
              selected
            </Badge>

            <Select value={bulkTemplateId} onValueChange={setBulkTemplateId}>
              <SelectTrigger className="h-9 w-[240px]">
                <SelectValue placeholder="Pick a template…" />
              </SelectTrigger>
              <SelectContent>
                {summaries.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {t.isDefault ? ' · default' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              size="sm"
              disabled={!bulkTemplateId || bulkMutation.isPending}
              onClick={handleApply}
            >
              {bulkMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {bulkMutation.isPending ? 'Applying…' : 'Apply & Recompute'}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              disabled={bulkMutation.isPending}
              onClick={() => {
                setSelectedIds(new Set())
                setBulkTemplateId('')
              }}
            >
              Clear selection
            </Button>
          </div>
        </div>
      )}

      <ConfirmationDialog
        open={deleteTarget !== null}
        onOpenChange={open => {
          if (!open) setDeleteTarget(null)
        }}
        title="Delete Calendar"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={async () => {
          if (!deleteTarget) return
          await deleteMutation.mutateAsync(deleteTarget.id)
          setDeleteTarget(null)
        }}
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
